import { Avatar, Badge, Box, Button, Text, VStack } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight, Building2 } from 'lucide-react'

interface CustomerCardProps {
  img?: string
  name: string
  industry: string
  link: string
  slug?: string
}

// Customer success card for the home-page marquee. Styled to match the DayN
// InsightCard language (bg.panel surface, semantic border, subtle shadow,
// hover lift + brand border) using Chakra semantic tokens so it adapts to
// light and dark mode.
export default function CustomerCard({ img, name, industry, link, slug }: CustomerCardProps) {
  const navigate = useNavigate()
  const currentPath = window.location.pathname
  const linkPath = slug ? `/customers/${slug}` : `/${link}`
  const go = () => navigate({ to: linkPath, search: { from: currentPath } })

  return (
    <Box
      as="figure"
      w="250px"
      mx={3}
      p={5}
      bg="bg.panel"
      borderWidth="1px"
      borderColor="border"
      borderRadius="lg"
      boxShadow="sm"
      cursor="pointer"
      transition="transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'md', borderColor: 'brand.solid' }}
      onClick={go}
    >
      <VStack gap={3}>
        {/* Logo sits in a neutral rounded tile so every logo lines up the same */}
        <Avatar.Root
          size="2xl"
          borderRadius="xl"
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border"
        >
          <Avatar.Image
            src={img}
            alt={name}
            style={{ objectFit: 'contain', padding: '10px', borderRadius: 'inherit' }}
          />
          <Avatar.Fallback color="content.tertiary">
            <Building2 size={28} />
          </Avatar.Fallback>
        </Avatar.Root>

        <Text fontWeight="semibold" color="content.primary" textAlign="center" lineHeight="1.2">
          {name}
        </Text>

        <Badge
          bg="bg.subtle"
          color="content.secondary"
          borderRadius="lg"
          px={2.5}
          py={0.5}
          fontSize="xs"
          fontWeight="semibold"
        >
          {industry}
        </Badge>

        <Button
          variant="outline"
          size="sm"
          w="100%"
          borderColor="border"
          fontWeight="medium"
          aria-label={`Explore ${name}`}
          onClick={(e) => {
            e.stopPropagation()
            go()
          }}
          _hover={{ '& svg': { transform: 'translateX(3px)' } }}
        >
          Explore Case Study
          <ArrowRight size={16} style={{ transition: 'transform 0.2s ease' }} />
        </Button>
      </VStack>
    </Box>
  )
}
